/**
 * ConditionalAuthProvider - WorkOS AuthKit Provider Wrapper
 *
 * This replaces the original Better Auth provider with WorkOS AuthKit.
 * It provides the same interface expected by the app components.
 */

import React from 'react';
import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { createLogger } from '../utils/logger-ssr-safe';

const logger = createLogger({ module: 'ConditionalAuthProvider' });

// Environment variables
const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID;
const WORKOS_REDIRECT_URI = import.meta.env.VITE_WORKOS_REDIRECT_URI || `${typeof window !== 'undefined' ? window.location.origin : ''}/callback`;

/**
 * Auth context that wraps WorkOS AuthKit
 */
export function ConditionalAuthProvider({ children }: { children: React.ReactNode }) {
  if (!WORKOS_CLIENT_ID) {
    logger.warn('VITE_WORKOS_CLIENT_ID not configured - auth will not work');
    // Return children without auth provider in dev mode
    return <>{children}</>;
  }

  return (
    <AuthKitProvider
      clientId={WORKOS_CLIENT_ID}
      redirectUri={WORKOS_REDIRECT_URI}
    >
      {children}
    </AuthKitProvider>
  );
}

// Re-export useAuth from WorkOS for components that use it
export { useAuth } from '@workos-inc/authkit-react';

// Default export for compatibility
export default ConditionalAuthProvider;
